import { Pipeline } from 'app/model/pipeline/pipeline';
import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { IValidatorFn, Named, ValidationConstants, Constants, shellStringResources } from 'shell-models-lib';
import { format } from 'app/shared/utils/string.util';

import { RequiredValidatorFactoryService } from 'app/extensibility/services/validator-factory/required-validator-factory.service';
import { PatternValidatorFactoryService } from 'app/extensibility/services/validator-factory/pattern-validator-factory.service';
import { StringLengthValidatorFactoryService } from 'app/extensibility/services/validator-factory/string-length-validator-factory.service';
import { UniqueNameValidatorFactoryService } from 'app/extensibility/services/validator-factory/unique-name-validator-factory.service';
import { DuplicatesValidatorFactoryService } from 'app/extensibility/services/validator-factory/duplicates-validator-factory.service';

import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { ModelService } from 'app/model/services/model.service';
import { Metamodel } from 'app/model/base/model';
import { ExecuteDataFlowActivity } from 'app/model/pipeline/execute-dataflow.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { map, firstValueFrom } from 'rxjs';
import { UserDefinedFunction } from 'app/model/dataflow/user-defined-function';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { Injector } from '@angular/core';

export function getParentActivity(pipeline: Owned): Activity {
    let entity = pipeline;
    while (entity === pipeline || !(entity instanceof Activity)) {
        if (entity.owner === undefined) {
            return undefined;
        }
        entity = entity.owner;
    }
    return entity as Activity;
}

export function getParentPipeline(pipeline: Owned): Pipeline {
    let entity = pipeline;
    while (entity === pipeline || !(entity instanceof Pipeline)) {
        if (entity.owner === undefined) {
            return undefined;
        }
        entity = entity.owner;
    }
    return entity as Pipeline;
}

export function getRootOwned<T>(pipeline: Owned): T {
    let entity = pipeline;
    while (entity.owner) {
        entity = entity.owner;
    }
    return entity as T;
}

//get all existing checkpoint other than the one from the activity itself.
export function getAllExistingCheckpoint(dataflowActivity: ExecuteDataFlowActivity, services: { modelService: ModelService }): Promise<string[]> {

    const observableCheckpoint = services.modelService.list(undefined, Metamodel.modelFor(Pipeline)).pipe(
        map((observables) => observables.map((o) => o as Pipeline)),
        map((pipelines) => pipelines.map((pipeline) => pipeline.activities)),
        map((activitySet) => {
            const executeDataFlowActivities = [];
            for (const activitySetValue of activitySet.values()) {
                for (const activity of activitySetValue) {
                    if (activity instanceof ExecuteDataFlowActivity) {
                        executeDataFlowActivities.push(activity);
                    }
                }
            }
            return executeDataFlowActivities;
        }),
        map((activityList) => {
            return activityList.filter((activity) => activity.customizedCheckpointKey
                && !ValueAdapter.isTemplated(activity, ExecuteDataFlowActivity.CUSTOMIZED_CHECKPOINT_KEY)
                // make sure it is not the same data flow activity you are trying to modify if one is provided
                && activity.name !== dataflowActivity.name)
                .map((activity) => {
                    return activity.customizedCheckpointKey;
                });
        })
    );

    return firstValueFrom(observableCheckpoint);
}

export function generalRequiredAndUniqueNameValidator(entity: Named, artifactTypeName: string, aArtifact: string, injector: Injector): IValidatorFn[] {
    return [
        new RequiredValidatorFactoryService().createNameValidator(artifactTypeName),
        new UniqueNameValidatorFactoryService().createValidatorNew(entity, aArtifact, injector)
    ];
}

/**
 * Artifact name validator
 * The validation functions include:
 * 1. Should not be empty
 * 2. Should start/end with alpha, number, underscore
 * 3. Max length is 140
 * 4. Should only contain alpha, number, underscore, space and dash
 * 5. Unique name in model service
 * @param entity Artifact named
 * @param artifactTypeName artifact type name after localization
 * @param aArtifact artifact type name with 'a' after localization
 */
export function generalNameValidator(entity: Named, artifactTypeName: string, aArtifact: string, injector: Injector): IValidatorFn[] {
    return [
        ...generalRequiredAndUniqueNameValidator(entity, artifactTypeName, aArtifact, injector),
        new PatternValidatorFactoryService().createValidatorStartEnd(ValidationConstants.startEndWithAlphaNumericUnderscoreValidator,
            format(shellStringResources.Validator_NamePattern_StartEnd_AlphanumericUnderscore, artifactTypeName)),
        new StringLengthValidatorFactoryService().maxLengthValidator(ValidationConstants.maxEntityNameLength, artifactTypeName),
        new PatternValidatorFactoryService().createValidator(ValidationConstants.allSupportedCharValidator,
            format(stringResources.Validator_NamePattern_All_AlphanumericUnderscoreDashSpace, artifactTypeName))
    ];
}

export function activityNameVaidatorWithoutDup(): IValidatorFn[] {
    return [
        new RequiredValidatorFactoryService().createNameValidator(stringResources.Activity),
        new PatternValidatorFactoryService().createValidatorStartEnd(ValidationConstants.startWithAlphaNumericUnderscoreValidator,
            format(shellStringResources.Validator_NamePattern_Start_AlphanumericUnderscore, stringResources.Activity)),
        new StringLengthValidatorFactoryService().maxLengthValidator(ValidationConstants.maxActivityNameLength, stringResources.Activity),
        new PatternValidatorFactoryService().createValidator(ValidationConstants.allSupportedCharValidator,
            format(stringResources.Validator_NamePattern_All_AlphanumericUnderscoreDashSpace, stringResources.Activity)),
        new PatternValidatorFactoryService().createValidator(ValidationConstants.noTrailingSpaceValidator,
            format(shellStringResources.Validator_NamePattern_No_TrailingSpace, stringResources.Activity))
    ];
}

export async function customizedCheckpointKeyValidator(dataflowActivity: ExecuteDataFlowActivity, services: { modelService: ModelService }): Promise<IValidatorFn[]> {
    return await getAllExistingCheckpoint(dataflowActivity, services).then((checkpoints) => {
        return [
            new RequiredValidatorFactoryService().createNameValidator(stringResources.CustomizedCheckpointKey),
            new StringLengthValidatorFactoryService().maxLengthValidator(ValidationConstants.maxActivityNameLength, stringResources.CustomizedCheckpointKey),
            new PatternValidatorFactoryService().createValidator(ValidationConstants.strictlyNoSpaceNameValidator, stringResources.CustomizedCheckpointKey_NoSpaces),
            new DuplicatesValidatorFactoryService().createValidator(
                checkpoints,
                (a: string, b: string) => a === b,
                format(stringResources.DuplicateCheckpointKey_Error, stringResources.CustomizedCheckpointKey),
                true
            )
        ];
    });
}

export function pipelineNamePrefixValidator(): IValidatorFn[] {
    return [
        new RequiredValidatorFactoryService().createNameValidator(stringResources.CopyWizard_Properties_NamePrefix),
        // start with alphanumeric & underscore check is unnecessary, as all characters must be alphanumeric & underscore.
        new PatternValidatorFactoryService().createValidator(ValidationConstants.alphanumericUnderscore,
            format(stringResources.Validator_NamePattern_All_AlphanumericUnderscore, stringResources.CopyWizard_Properties_NamePrefix)),
        new StringLengthValidatorFactoryService().maxLengthValidator(ValidationConstants.maxEntityNamePrefixLength, stringResources.CopyWizard_Properties_NamePrefix)
    ];
}

export function datasetNameValidator(entity: Named, injector: Injector): IValidatorFn[] {
    return [
        new RequiredValidatorFactoryService().createNameValidator(stringResources.ModelName_DataSet),
        new PatternValidatorFactoryService().createValidatorStartEnd(ValidationConstants.startEndWithAlphaNumericValidator,
            format(shellStringResources.Validator_NamePattern_StartEnd_Alphanumeric, stringResources.ModelName_DataSet)),
        new StringLengthValidatorFactoryService().maxLengthValidator(ValidationConstants.maxEntityNameLength, stringResources.ModelName_DataSet), // max
        new StringLengthValidatorFactoryService().minLengthValidator(ValidationConstants.minDatasetNameLength, stringResources.ModelName_DataSet), // min
        new PatternValidatorFactoryService().createValidator(ValidationConstants.alphanumericUnderscore,
            format(stringResources.Validator_NamePattern_All_AlphanumericUnderscore, stringResources.ModelName_DataSet)),
        new UniqueNameValidatorFactoryService().createValidatorNew(entity, stringResources.aDataset, injector)
    ];
}

export function lsNameValidator(modelService: ModelService, entity?: Named, noDash: boolean = true): IValidatorFn[] {
    const validator = [
        new RequiredValidatorFactoryService().createNameValidator(__IS_FABRIC_DPE__ ? stringResources.ConnectionLabel : stringResources.LinkedService),
        new PatternValidatorFactoryService().createValidatorStartEnd(ValidationConstants.startEndWithAlphaNumericUnderscoreValidator,
            format(shellStringResources.Validator_NamePattern_StartEnd_AlphanumericUnderscore,
                __IS_FABRIC_DPE__ ? stringResources.ConnectionLabel : stringResources.LinkedService)),
        new StringLengthValidatorFactoryService().maxLengthValidator(ValidationConstants.maxEntityNameLength,
            __IS_FABRIC_DPE__ ? stringResources.ConnectionLabel : stringResources.LinkedService),
        (name: string) => modelService.isUniqueName(name || '', Metamodel.modelFor(LinkedService), entity)
            ? null
            : format(stringResources.Validator_NameExist, __IS_FABRIC_DPE__ ? stringResources.aConnection : stringResources.aLinkedService, '',
                __IS_A365__ || __IS_FABRIC_DPE__ ? stringResources.WorkspaceLowerCase : stringResources.FactoryLowerCase)
    ];
    if (noDash) {
        validator.push(
            new PatternValidatorFactoryService().createValidator(ValidationConstants.noDashNameValidator,
                format(stringResources.Validator_NamePattern_All_AlphanumericUnderscoreSpace,
                    __IS_FABRIC_DPE__ ? stringResources.ConnectionLabel : stringResources.LinkedService))
        );
    } else {
        validator.push(
            new PatternValidatorFactoryService().createValidator(Constants.factoryEntityNameValidator,
                format(stringResources.Validator_NamePattern_All_AlphanumericUnderscoreDashSpace,
                    __IS_FABRIC_DPE__ ? stringResources.ConnectionLabel : stringResources.LinkedService))
        );
    }
    return validator;
}

export function folderNameValidator(siblingFolderNames: string[] = []): IValidatorFn[] {
    return [
        new RequiredValidatorFactoryService().createNameValidator(stringResources.Folder),
        new PatternValidatorFactoryService().createValidator(ValidationConstants.nonEmptyNameValidator, stringResources.FolderNameCantBeEmpty),
        new PatternValidatorFactoryService().createValidator(ValidationConstants.noSlashFolderNameValidator, stringResources.InvalidFolderName),
        new PatternValidatorFactoryService().createValidator(ValidationConstants.noHtmlFolderNameValidator, stringResources.Validator_NoHtml),
        new DuplicatesValidatorFactoryService().createValidator(
            siblingFolderNames.filter((s) => !!s), // Filter out empty ones
            (a: string, b: string) => (a || '').trim() === (b || '').trim(),
            stringResources.FolderNameExists,
            true
        )
    ];
}

export function userDefinedFunctionLibraryNameValidator(entity: Named): IValidatorFn[] {
    const artifactType = entity instanceof UserDefinedFunction ? stringResources.Function : stringResources.UDFLibrary;
    return [
        new RequiredValidatorFactoryService().createNameValidator(artifactType),
        new PatternValidatorFactoryService().createValidatorStartEnd(ValidationConstants.startEndWithAlphaNumericValidator,
            format(shellStringResources.Validator_NamePattern_StartEnd_Alphanumeric, artifactType)),
        new StringLengthValidatorFactoryService().maxLengthValidator(ValidationConstants.maxEntityNameLength, artifactType), // max
        new StringLengthValidatorFactoryService().minLengthValidator(ValidationConstants.minDatasetNameLength, artifactType), // min
        new PatternValidatorFactoryService().createValidator(ValidationConstants.alphanumericUnderscore,
            format(stringResources.Validator_NamePattern_All_AlphanumericUnderscore, artifactType)),
        new PatternValidatorFactoryService().createValidator(ValidationConstants.noDashNameValidator,
            format(stringResources.Validator_NamePattern_All_AlphanumericUnderscoreSpace, artifactType))
    ];
}

export function factoryNameValidator(): IValidatorFn[] {
    return [
        new RequiredValidatorFactoryService().createNameValidator(stringResources.Factory),
        new PatternValidatorFactoryService().createValidatorStartEnd(ValidationConstants.startEndWithAlphaNumericValidator,
            format(shellStringResources.Validator_NamePattern_StartEnd_Alphanumeric, stringResources.Factory)),
        new StringLengthValidatorFactoryService().maxLengthValidator(63, stringResources.Factory), // max
        new StringLengthValidatorFactoryService().minLengthValidator(3, stringResources.Factory), // min
        new PatternValidatorFactoryService().createValidator(ValidationConstants.alphanumericDash,
            format(stringResources.Validator_NamePattern_All_AlphanumericDash, stringResources.Factory)),
    ];
}

export function folderPathBaseValidator(lsType?: string): IValidatorFn[] {
    // spoke with Tina, only limited connectors (ie: Blob/Gen2) supports wildcard chars
    const supportSpecialChar = lsType ?
        [adfModelTypeMap.azureBlob.linkedServiceType, adfModelTypeMap.azureBlobFS.linkedServiceType].includes(lsType) : false;
    return [
        new PatternValidatorFactoryService().createValidator(
            supportSpecialChar ? Constants.folderNameValidation : Constants.folderNameStricterValidation, stringResources.FolderInvalidNamePattern),
        new PatternValidatorFactoryService().createValidator(Constants.folderNameValidation2, stringResources.FolderInvalidDotNamePattern)
    ];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function validateValue(value: any, validators: IValidatorFn[]): boolean {
    if (validators) {
        for (const validator of validators) {
            // If the validator does not return null, it means that the value is invalid.
            if (validator(value)) {
                return false;
            }
        }
    }
    return true;
}
